import { create } from 'zustand'
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  Timestamp,
  doc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { Survey } from '../models/Survey'

interface SurveyState {
  allSurveys: Survey[]
  userSurveys: Survey[]
  loading: boolean
  error: string | null
  fetchAllSurveys: (users: { id: string; displayName: string }[]) => Promise<void>
  fetchUserSurveys: (userId: string) => Promise<void>
  clearUserSurveys: () => void
  deleteSurvey: (id: string) => Promise<void>
  deleteSurveysBatch: (ids: string[]) => Promise<void>
}

export const useSurveyStore = create<SurveyState>((set) => ({
  allSurveys: [],
  userSurveys: [],
  loading: false,
  error: null,

  fetchAllSurveys: async (users) => {
    set({ loading: true, error: null })
    try {
      const userMap = new Map(users.map((u) => [u.id, u.displayName]))
      const q = query(collection(db, 'surveys'), orderBy('completedAt', 'desc'))
      const snapshot = await getDocs(q)

      const surveys: Survey[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          userId: data.userId,
          surveyType: data.surveyType,
          answers: data.answers || {},
          scores: data.scores || {},
          dominantDimension: data.dominantDimension || '',
          completedAt: (data.completedAt as Timestamp).toDate(),
          version: data.version || '',
          userName: userMap.get(data.userId),
          startedAt: data.startedAt ? (data.startedAt as Timestamp).toDate() : undefined,
          durationSeconds: data.durationSeconds ?? undefined,
          questionTimings: data.questionTimings ?? undefined,
          isRetake: data.isRetake ?? undefined,
          entrySource: data.entrySource ?? undefined,
          pauseCount: data.pauseCount ?? undefined,
          totalPauseSeconds: data.totalPauseSeconds ?? undefined,
        }
      })

      set({ allSurveys: surveys, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入量表資料失敗',
      })
    }
  },

  fetchUserSurveys: async (userId) => {
    set({ loading: true, error: null })
    try {
      const q = query(
        collection(db, 'surveys'),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc')
      )
      const snapshot = await getDocs(q)

      const surveys: Survey[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          userId: data.userId,
          surveyType: data.surveyType,
          answers: data.answers || {},
          scores: data.scores || {},
          dominantDimension: data.dominantDimension || '',
          completedAt: (data.completedAt as Timestamp).toDate(),
          version: data.version || '',
          startedAt: data.startedAt ? (data.startedAt as Timestamp).toDate() : undefined,
          durationSeconds: data.durationSeconds ?? undefined,
          questionTimings: data.questionTimings ?? undefined,
          isRetake: data.isRetake ?? undefined,
          entrySource: data.entrySource ?? undefined,
          pauseCount: data.pauseCount ?? undefined,
          totalPauseSeconds: data.totalPauseSeconds ?? undefined,
        }
      })

      set({ userSurveys: surveys, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入用戶量表資料失敗',
      })
    }
  },

  clearUserSurveys: () => {
    set({ userSurveys: [] })
  },

  deleteSurvey: async (id) => {
    set({ error: null })
    try {
      await deleteDoc(doc(db, 'surveys', id))
      set((state) => ({
        allSurveys: state.allSurveys.filter((s) => s.id !== id),
        userSurveys: state.userSurveys.filter((s) => s.id !== id),
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '刪除量表失敗',
      })
      throw error
    }
  },

  deleteSurveysBatch: async (ids) => {
    if (ids.length === 0) return
    set({ error: null })
    try {
      // Firestore writeBatch 上限 500，分塊處理
      const CHUNK = 450
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK)
        const batch = writeBatch(db)
        chunk.forEach((id) => batch.delete(doc(db, 'surveys', id)))
        await batch.commit()
      }
      const idSet = new Set(ids)
      set((state) => ({
        allSurveys: state.allSurveys.filter((s) => !idSet.has(s.id)),
        userSurveys: state.userSurveys.filter((s) => !idSet.has(s.id)),
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '批次刪除量表失敗',
      })
      throw error
    }
  },
}))
